import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { setApiAppStateBridge } from '../utils/Api';

// Create Context
const AppContext = createContext();

// Hook to use App Context
export const useAppContext = () => useContext(AppContext);

// Provider Component
export const AppProvider = ({ children }) => {
  const initialState = {
    loading: false,
    error: null,
    success: null,
    accessToken: '',
    refreshToken: '',
    requestSet: [],
    user: null,
    isAuthenticated: false
  };

  const savedState = localStorage.getItem('appContextState');
  const [appState, setAppState] = useState(() => {
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      return {
        ...initialState,
        ...parsedState,
        loading: false,
        requestSet: Array.isArray(parsedState.requestSet) ? parsedState.requestSet : [],
      };
    }
    return initialState;
  });
  const appStateRef = useRef(appState);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      ...appState,
      requestSet: Array.isArray(appState.requestSet) ? appState.requestSet : [],
    };
    localStorage.setItem('appContextState', JSON.stringify(stateToSave));
  }, [appState]);

  const updateAppState = useCallback((updates) => {
    setAppState((prevState) => {
      const nextState = { ...prevState, ...updates };
      appStateRef.current = nextState;
      return nextState;
    });
  }, []);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  useEffect(() => {
    setApiAppStateBridge({
      getState: () => appStateRef.current,
      updateState: updateAppState,
    });
  }, [updateAppState]);

  const contextValue = useMemo(() => ({ appState, updateAppState }), [appState, updateAppState]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
